/**
 * One label per PO-line date concept.
 *
 * Three concepts, three names, no fourth (Aaron, 10 Sep 2026), plus the ERP's
 * own schedule date, named here so it cannot be mistaken for a rival:
 *
 * - **Original Required Date (ODD)** — `original_promise_date`. The date the
 *   order was originally required by. It is what the OTIF/OTD verdict, the
 *   delivery window and the arrears test are judged against, and it does not
 *   move when a delivery is re-agreed. A surface labelled ODD shows the
 *   server's `odd_date` — the engine's anchor (CR-7269).
 * - **Customer Required Date** — `delivery_date`. The date currently required,
 *   which does move.
 * - **Latest Supplier Promise Date** — `supplier_promise_date`. What the
 *   supplier has most recently committed to.
 * - **MRP/ERP Scheduled Date** — `mrp_scheduled_date`. The ERP's MRP schedule
 *   date; filled only by a feed. No metric reads it.
 *
 * Every screen that names one of these dates imports its label from here.
 * Without a single source a tenth screen mints an eleventh name — which is how
 * the product came to carry "ODD", "Agreed Date (ODD)", "ODD (Original Due
 * Date)", "Original Promise", "Requested Date" and "Current Due Date" for two
 * underlying columns.
 *
 * There are NO short forms (15 Sep 2026): the `_SHORT` names below are kept
 * for their importers and each IS the full label, so a narrow column shows
 * the same words as every other screen.
 *
 * The backend's copy is `backend/purchasing/date_labels.py`, and
 * `tests/purchasing/test_date_labels.py` checks the two against each other.
 */

/** `original_promise_date` — the date the verdict is judged against. */
export const ORIGINAL_REQUIRED_DATE = 'Original Required Date (ODD)';

/** `delivery_date` — the date currently required, which moves. */
export const CUSTOMER_REQUIRED_DATE = 'Customer Required Date';

/** `supplier_promise_date` — what the supplier has most recently committed to. */
export const SUPPLIER_PROMISE_DATE = 'Latest Supplier Promise Date';

/** `mrp_scheduled_date` — the ERP's MRP schedule date. */
export const MRP_SCHEDULED_DATE = 'MRP/ERP Scheduled Date';

/**
 * The GRN's own receipt date. NOT one of the four PO-line dates above — it is
 * what a delivery ACTUALLY arrived on, the thing every metric compares them to.
 * Named here for the same reason they are (R-380): the drill-down registry was
 * calling one column by SIX names, and the Deliveries grid headed it 'Delivery
 * Date' while its OWN csvHeader already said 'Receipt Date'. Mirrors
 * purchasing.date_labels.RECEIPT_DATE; the two trees are checked against each
 * other by tests/purchasing/test_date_labels.py.
 */
export const RECEIPT_DATE = 'Receipt Date';

// DATE-FIELD-CANON PR 2 (15 Sep 2026): NO short forms — one label per date, everywhere. The
// _SHORT names stay exported so nothing breaks, but each IS the full label; the
// backend test pins the two maps equal.
export const ORIGINAL_REQUIRED_DATE_SHORT = ORIGINAL_REQUIRED_DATE;
export const CUSTOMER_REQUIRED_DATE_SHORT = CUSTOMER_REQUIRED_DATE;
export const SUPPLIER_PROMISE_DATE_SHORT = SUPPLIER_PROMISE_DATE;
export const MRP_SCHEDULED_DATE_SHORT = MRP_SCHEDULED_DATE;

/**
 * Names these dates have been shown under, which no screen may use again.
 * "Current due date" is banned outright (Aaron, 10 Sep 2026); the rest are
 * retired synonyms that made two columns read as six concepts. The backend
 * test scans this tree for every one of them.
 */
export const RETIRED_DATE_LABELS: readonly string[] = [
  'Current Due Date',
  'Current due date',
  'Agreed Date (ODD)',
  'ODD (Original Due Date)',
  'Original Promise',
  'Original promise',
  'Delivery date (ODD)',
  'Delivery date (current due)',
  'Original Due Date (ODD)',
  // The supplier-performance drill-down name for the promise date (PR 2).
  'Review Date (LSP)',
];
